import React, { useState, useEffect, useRef } from "react";
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from "@mui/material";
import Icon from '@mui/material/Icon';
import "./NodeList.css";

import { TextField, Button } from "@mui/material";
import { useNodesServiceClient } from "../../services/brainflex/NodesServiceClient";
import type { ContextNode, Node, RecordUserPlaysSpeechOutput, BookmarkSpeechNodeOutput } from "../../services/brainflex/NodesServiceClient";
import ServiceTransaction from "../../services/ServiceTransaction";
import { useLanguageSelection } from "../../contexts/LanguageSelectionContext";
import NodeItem from "../../components/NodeItem";

interface NodeListProps {
  nodes: ContextNode[];
  onRequestListRefresh: () => void; // Callback for refreshing the list
}

const NodeList: React.FC<NodeListProps> = ({ nodes, onRequestListRefresh }) => {
  // State
  const [newNodeText, setNewNodeText] = useState("");

  // Context
  const { languageSelection } = useLanguageSelection();

  // Other
  const audioRefs = useRef<{ [id: number]: HTMLAudioElement | null }>({});
  const playButtonRefs = useRef<{ [id: number]: HTMLButtonElement | null }>({});
  const nodesServiceClient = useNodesServiceClient();

  useEffect(() => {
    // This effect runs whenever languageSelection context changes
  }, [languageSelection]);

  const handleAddNewSubmit = async () => {
    try {
      // Returns a node?
      const transaction: ServiceTransaction<Node> = await nodesServiceClient.postAddNode(newNodeText);
      if (transaction.response.isSuccess) {
        // handle data  
        setNewNodeText(""); // Clear input field
        onRequestListRefresh(); // Notify parent to refresh the list
      }
      else {
        // handle error
      }
    } catch (error) {
      console.error("Error creating node: ", error);
    }
  };

  // request translation
  const handleTranslateButtonClick = async (nodeId: number, language: string) => {
    try {
      const transaction: ServiceTransaction<Node> = await nodesServiceClient.postAddNodeText(nodeId, language);
      if (transaction.response.isSuccess) {
        // handle data  
        onRequestListRefresh(); // Notify parent to refresh the list
      }
      else {
        // handle error
      }
    } catch (error) {
      console.error("Error creating node: ", error);
    }
  };

  // request recording
  const handleSpeakButtonClick = async (nodeId: number, language: string, locale: string, voice: string) => {
    try {
      const transaction: ServiceTransaction<Node> = await nodesServiceClient.postAddNodeSpeech(nodeId, language, locale, voice);
      if (transaction.response.isSuccess) {
        // handle data  
        onRequestListRefresh(); // Notify parent to refresh the list
        //loadAudio(nodeId, language, locale, voice, false);
      }
      else {
        // handle error
      }
    } catch (error) {
      console.error("Error creating node: ", error);
    }
  };

  // request play
  const handlePlayButtonClick = async (nodeId: number, language: string, locale: string, voice: string) => {
    loadAudio(nodeId, language, locale, voice, true);
  };

  const handleAudioPlayedClick = async (nodeId: number, locale: string, voice: string) => {
    console.log("Audio play pressed!", nodeId)
    try {
      const transaction: ServiceTransaction<RecordUserPlaysSpeechOutput> = await nodesServiceClient.putRecordUserPlaysSpeech(nodeId, locale, voice);
      if (transaction.response.isSuccess) {
        // handle data
        // int returned is play count, but we don't need it here
      }
      else {
        // handle error
      }
    } catch (error) {
      console.error("Error recording speech playback: ", error);
    }
  };

  const handleBookmarkSpeechClick = async (nodeId: number, locale: string, voice: string, isBookmarked: boolean) => {
    try {
      const transaction: ServiceTransaction<BookmarkSpeechNodeOutput> = await nodesServiceClient.putBookmarkSpeech(nodeId, locale, voice, isBookmarked);
      if (transaction.response.isSuccess) {
        // handle data - refresh the list to get updated bookmark status
        onRequestListRefresh();
      }
      else {
        // handle error
      }
    } catch (error) {
      console.error("Error bookmarking speech: ", error);
    }
  };


  const loadAudio = async (nodeId: number, language: string, locale: string, voice: string, playIt: boolean) => {
    try {
      const transaction: ServiceTransaction<Blob> = await nodesServiceClient.getNodeSpeechBinary(nodeId, language, locale, voice);
      if (transaction.response.isSuccess) {
        // handle data
        var blob: Blob = transaction.response.success!.data!;
        //let audioBlob = new Blob([binaryArray], { type: 'audio/wav' });
        
        // Create a URL for the audio blob
        let audioUrl = URL.createObjectURL(blob);

        // Switch on audio element
        const audioElement = audioRefs.current[nodeId];
        if (audioElement) {
          audioElement.src = audioUrl;
          audioElement.load();
          //audioElement.style.display = "block";
          audioElement.style.visibility = "visible";
          if (playIt)
            audioElement.play();
        }

        // Switch off play button element now the audio is there
        const playButtonElement = playButtonRefs.current[nodeId];
        if (playButtonElement) {
          playButtonElement.style.display = "none";
        }
      }
      else {
        // handle error
      }
    } catch (error) {
      console.error("Error loading audio: ", error);
    }
  };

  return (
    <>
      {!languageSelection ? (
        <>
          <p>Select a <strong>target language</strong> to see the dictionary entries. Choose a <strong>locale</strong> and a <strong>voice</strong> for the audio.</p>
        </>
      ) : (
        <>
          <h2>Entries</h2>
          <p>Entries for language <strong>{languageSelection.language}</strong>, spoken in <strong>{languageSelection.locale}</strong> locale using voice <strong>{languageSelection.voice}</strong></p>
          <TableContainer component={Paper} style={{ display: "inline-block", width: "auto" }}>
          <Table style={{ width: "auto" }}>
            <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Translation</TableCell>
              <TableCell>Speech</TableCell>
              <TableCell>Bookmark</TableCell>
              <TableCell>View</TableCell>
            </TableRow>
            </TableHead>
            <TableBody>
            <TableRow>
              <TableCell>
                <Icon>add</Icon>
              </TableCell>
              <TableCell>
                <TextField label="English text" variant="outlined" value={newNodeText} onChange={(e) => setNewNodeText(e.target.value)} />
              </TableCell>
              <TableCell>
                {/* name */}
              </TableCell>
              <TableCell>
                {/* speech */}
              </TableCell>
              <TableCell>
                {/* bookmark */}
              </TableCell>
              <TableCell>
                <Button variant="contained" color="primary" onClick={handleAddNewSubmit}>Add</Button>
              </TableCell>
            </TableRow>
            {nodes.map((node) => (
              <React.Fragment key={node.id}>
                <NodeItem
                  node={node}
                  languageSelection={languageSelection}
                  playButtonRef={(el: HTMLButtonElement | null) => void (playButtonRefs.current[node.id] = el)}
                  audioRef={(el: HTMLAudioElement | null) => void (audioRefs.current[node.id] = el)}
                  onTranslate={handleTranslateButtonClick}
                  onSpeak={handleSpeakButtonClick}
                  onPlay={handlePlayButtonClick}
                  onAudioPlayed={handleAudioPlayedClick}
                  onBookmark={handleBookmarkSpeechClick}
                />
              </React.Fragment>
            ))}
            </TableBody>
          </Table>
          </TableContainer>
        </>
      )}
    </>
  );
};

export default NodeList;